import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CreditCard, AlertCircle, Info, Plus, TrendingDown, Banknote, Calendar } from "lucide-react";
import { ApiError } from "../../../lib/api";
import { clsx } from "clsx";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ToastContainer } from "../../../components/ui/Toast";
import { useToast } from "../../../hooks/useToast";
import { useAuth } from "../../../hooks/useAuth";
import { useMyLoans, useApplyLoan, useLoanConfig, useMyProfile } from "../../../hooks/useLoan";
import type { Loan, LoanStatus } from "../../../lib/loanApi";
import { formatCurrency, formatDate, formatPeriod } from "../../../utils/format";

// ── Constants ────────────────────────────────────────────────────────────────

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-xs text-[#dc2626] mt-1";

const STATUS_LABELS: Record<LoanStatus, string> = {
  PENDING:  "Pending",
  APPROVED: "Approved",
  ACTIVE:   "Active",
  REJECTED: "Rejected",
  PAID:     "Settled",
};

const STATUS_BADGE: Record<LoanStatus, string> = {
  PENDING:  "bg-[#fef9c3] text-[#a16207]",
  APPROVED: "bg-[#dbeafe] text-[#1d4ed8]",
  ACTIVE:   "bg-[#dcfce7] text-[#15803d]",
  REJECTED: "bg-[#fee2e2] text-[#b91c1c]",
  PAID:     "bg-[#f1f5f9] text-[#64748b]",
};

const LOAN_STATUS_COLORS: Record<LoanStatus, string> = {
  PENDING:  "border-l-[#2447d9] bg-[#fafbff]",
  APPROVED: "border-l-[#f59e0b] bg-[#fffdf7]",
  ACTIVE:   "border-l-[#16a34a] bg-[#fafffe]",
  REJECTED: "border-l-[#dc2626] bg-[#fffafa]",
  PAID:     "border-l-[#94a3b8] bg-[#f8fafc]",
};

// ── Period helpers ────────────────────────────────────────────────────────────

function periodToIndex(period: string): number {
  const m = period.match(/^(\d{4})-(\d{2})-(H[12])$/);
  if (!m) return 0;
  return parseInt(m[1]) * 24 + (parseInt(m[2]) - 1) * 2 + (m[3] === "H2" ? 1 : 0);
}

function currentPeriodIndex(): number {
  const now = new Date();
  return now.getFullYear() * 24 + now.getMonth() * 2 + (now.getDate() >= 16 ? 1 : 0);
}

// ── Apply Loan Modal ──────────────────────────────────────────────────────────

interface ApplyLoanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function ApplyLoanModal({ isOpen, onClose, onSuccess }: ApplyLoanModalProps) {
  const applyLoan = useApplyLoan();
  const { user, activeBusinessId } = useAuth();
  const employeeName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.username;

  const { data: config, isLoading: configLoading } = useLoanConfig(activeBusinessId ?? "");
  const { data: myProfile } = useMyProfile();

  const termOptions = (config?.allowedTerms ?? [])
    .slice()
    .sort((a, b) => a - b)
    .map((t) => ({
      value: t,
      label: `${t} periods (${t / 2} month${t / 2 !== 1 ? "s" : ""})`,
    }));

  const schema = z.object({
    amount: z.number({ invalid_type_error: "Enter a valid amount" }).positive("Must be positive"),
    termPeriods: z.number().int().min(1),
    reason: z.string().min(1, "Reason is required").max(500),
  });

  const { register, handleSubmit, reset, watch, setError, formState: { errors, isSubmitting } } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      termPeriods: termOptions[0]?.value ?? 6,
    },
  });

  const amount = watch("amount");
  const termPeriods = watch("termPeriods");

  const installmentPreview = (() => {
    if (!amount || !termPeriods || amount <= 0 || termPeriods <= 0 || !config) return null;
    const rate = config.interestRate ?? 0;
    const termYears = termPeriods / 24;
    const totalInterest = rate > 0 ? amount * (rate / 100) * termYears : 0;
    const total = amount + totalInterest;
    const installment = Math.round((total / termPeriods) * 100) / 100;
    return { installment, totalInterest, total };
  })();

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (!activeBusinessId) return;
    try {
      await applyLoan.mutateAsync({
        businessId: activeBusinessId,
        amount: data.amount,
        termPeriods: data.termPeriods,
        reason: data.reason,
        employeeName: employeeName ?? undefined,
      });
      reset();
      onSuccess();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      if (code === "EXCEEDS_REPAYMENT_CAPACITY") {
        setError("root", { message: "Your loan installment would exceed 80% of your bi-monthly salary. Try a lower amount or longer repayment term." });
      } else if (code === "AMOUNT_EXCEEDS_LIMIT") {
        setError("root", { message: `Loan amount exceeds the configured maximum of ${formatCurrency(config!.maxLoanableAmount!)}.` });
      } else if (code === "INVALID_TERM") {
        setError("root", { message: "Selected repayment term is not allowed. Please choose a valid term." });
      } else if (code === "LOAN_CONFIG_NOT_FOUND") {
        setError("root", { message: "Loan applications are not available. Please contact your administrator." });
      } else {
        setError("root", { message: "Failed to submit loan application. Please try again." });
      }
    }
  };

  const handleClose = () => { reset(); onClose(); };
  const noConfig = !configLoading && (!config || config.allowedTerms.length === 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Apply for Loan" size="md">
      {configLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : noConfig ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <AlertCircle className="h-10 w-10 text-[#94a3b8]" />
          <p className="text-sm font-medium text-[#0f172a]">Loan applications unavailable</p>
          <p className="text-xs text-[#64748b] max-w-xs">
            The administrator has not yet configured loan terms for this business. Please contact HR.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 flex items-center gap-4 text-xs text-[#64748b]">
            <span>Interest rate: <strong className="text-[#0f172a]">{config!.interestRate}% p.a. (flat)</strong></span>
            {config!.maxLoanableAmount && (
              <span>Max: <strong className="text-[#0f172a]">{formatCurrency(config!.maxLoanableAmount)}</strong></span>
            )}
          </div>

          <div>
            <label className={LABEL_CLASS}>Loan Amount</label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-[#64748b]">₱</span>
              <input
                {...register("amount", { valueAsNumber: true })}
                type="number" min={1000} max={config!.maxLoanableAmount ?? undefined} step={1}
                className={clsx(INPUT_CLASS, "pl-7")} placeholder="e.g. 50000"
              />
            </div>
            {errors.amount && <p className={ERROR_CLASS}>{errors.amount.message}</p>}
          </div>

          <div>
            <label className={LABEL_CLASS}>Repayment Term</label>
            <select {...register("termPeriods", { valueAsNumber: true })} className={INPUT_CLASS}>
              {termOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errors.termPeriods && <p className={ERROR_CLASS}>{errors.termPeriods.message}</p>}
          </div>

          {installmentPreview && (
            <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 space-y-1.5 text-xs">
              <p className="font-semibold text-[#0f172a] mb-1">Payment Summary</p>
              <div className="flex justify-between text-[#64748b]">
                <span>Principal</span><span>{formatCurrency(amount)}</span>
              </div>
              {installmentPreview.totalInterest > 0 && (
                <div className="flex justify-between text-[#64748b]">
                  <span>Total interest ({config!.interestRate}% × {(termPeriods / 24).toFixed(2)} yr)</span>
                  <span>{formatCurrency(installmentPreview.totalInterest)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-[#0f172a] border-t border-[#e2e8f0] pt-1.5">
                <span>Total repayable</span><span>{formatCurrency(installmentPreview.total)}</span>
              </div>
              <div className="flex justify-between font-bold text-[#2447d9] text-sm">
                <span>Per period deduction</span><span>{formatCurrency(installmentPreview.installment)}</span>
              </div>

              {myProfile?.baseSalary ? (() => {
                const semiMonthly = myProfile.baseSalary / 2;
                const afterLoan = semiMonthly - installmentPreview.installment;
                const capacityUsed = (installmentPreview.installment / (semiMonthly * 0.8)) * 100;
                const overCapacity = installmentPreview.installment > semiMonthly * 0.8;
                return (
                  <div className="border-t border-[#e2e8f0] pt-2 space-y-1.5">
                    <p className="font-semibold text-[#374151]">Estimated Take-Home Impact</p>
                    <div className="flex justify-between text-[#64748b]">
                      <span>Bi-monthly base pay</span>
                      <span>{formatCurrency(semiMonthly)}</span>
                    </div>
                    <div className="flex justify-between text-[#64748b]">
                      <span>Loan deduction / period</span>
                      <span className="text-[#dc2626]">− {formatCurrency(installmentPreview.installment)}</span>
                    </div>
                    <div className={`flex justify-between font-semibold border-t border-[#e2e8f0] pt-1 ${afterLoan < 0 ? "text-[#dc2626]" : "text-[#16a34a]"}`}>
                      <span>Estimated take-home</span>
                      <span>{formatCurrency(Math.max(0, afterLoan))}</span>
                    </div>
                    <div className={`flex items-center gap-1.5 pt-0.5 ${overCapacity ? "text-[#dc2626]" : "text-[#64748b]"}`}>
                      <Info className="h-3 w-3 shrink-0" />
                      <span>
                        Deduction is <strong>{capacityUsed.toFixed(0)}%</strong> of your 80% repayment capacity
                        {overCapacity ? " — exceeds limit" : " — within limit"}
                      </span>
                    </div>
                  </div>
                );
              })() : (
                <div className="flex items-start gap-1.5 pt-1 border-t border-[#e2e8f0] text-[#64748b]">
                  <Info className="h-3 w-3 shrink-0 mt-0.5" />
                  <span>Total loan deductions per period must not exceed <strong>80% of your bi-monthly salary</strong>.</span>
                </div>
              )}
            </div>
          )}

          <div>
            <label className={LABEL_CLASS}>Purpose / Reason</label>
            <textarea
              {...register("reason")} rows={3} maxLength={500}
              className={INPUT_CLASS} placeholder="Briefly describe the purpose of this loan..."
            />
            {errors.reason && <p className={ERROR_CLASS}>{errors.reason.message}</p>}
          </div>

          {errors.root && (
            <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">{errors.root.message}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} disabled={isSubmitting}
              className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting}
              className="flex-1 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {isSubmitting ? "Submitting…" : "Submit Application"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ── Loan Card ─────────────────────────────────────────────────────────────────

function LoanCard({ loan }: { loan: Loan }) {
  const colorClass = LOAN_STATUS_COLORS[loan.status] ?? "";
  const truncatedReason = loan.reason.length > 100 ? loan.reason.slice(0, 100) + "…" : loan.reason;

  // Repayment progress for ACTIVE loans
  const progress = (() => {
    if (loan.status !== "ACTIVE" || !loan.firstInstallmentPeriod || !loan.termPeriods) return null;
    const firstIdx = periodToIndex(loan.firstInstallmentPeriod);
    const curIdx = currentPeriodIndex();
    const elapsed = Math.max(0, Math.min(loan.termPeriods, curIdx - firstIdx + 1));
    const pct = loan.termPeriods > 0 ? (elapsed / loan.termPeriods) * 100 : 0;
    return { elapsed, total: loan.termPeriods, pct };
  })();

  return (
    <div className={clsx("rounded-xl border border-[#e2e8f0] border-l-4 overflow-hidden shadow-sm", colorClass)}>
      {/* Main content */}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-base font-bold text-[#0f172a]">{formatCurrency(loan.amount)}</span>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_BADGE[loan.status]}`}>
                {STATUS_LABELS[loan.status]}
              </span>
              {loan.termPeriods && (
                <span className="text-xs text-[#94a3b8]">{loan.termPeriods} periods</span>
              )}
            </div>
            <p className="text-sm text-[#374151] break-words">{truncatedReason}</p>
            <p className="mt-1 text-xs text-[#94a3b8]">Applied {formatDate(loan.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Active loan — repayment progress + schedule */}
      {loan.status === "ACTIVE" && (
        <div className="border-t border-[#e2e8f0] bg-[#f0fdf4] px-5 py-3 space-y-2.5">
          {/* Progress bar */}
          {progress && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="font-medium text-[#16a34a]">Repayment progress</span>
                <span className="text-[#64748b]">{progress.elapsed} of {progress.total} periods</span>
              </div>
              <div className="w-full bg-[#dcfce7] rounded-full h-2">
                <div
                  className="bg-[#16a34a] h-2 rounded-full transition-all"
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
            </div>
          )}

          {/* Deduction schedule */}
          {loan.installmentAmount && (
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <p className="text-[#64748b] mb-0.5">Per period</p>
                <p className="font-semibold text-[#0f172a]">{formatCurrency(loan.installmentAmount)}</p>
              </div>
              {loan.firstInstallmentPeriod && (
                <div>
                  <p className="text-[#64748b] mb-0.5">First</p>
                  <p className="font-semibold text-[#0f172a]">{formatPeriod(loan.firstInstallmentPeriod)}</p>
                </div>
              )}
              {loan.lastInstallmentPeriod && (
                <div>
                  <p className="text-[#64748b] mb-0.5">Last</p>
                  <p className="font-semibold text-[#0f172a]">{formatPeriod(loan.lastInstallmentPeriod)}</p>
                </div>
              )}
            </div>
          )}

          {!loan.firstInstallmentPeriod && (
            <p className="text-xs text-[#15803d] font-medium">
              Installment schedule active — deducted from payslip
            </p>
          )}
        </div>
      )}

      {/* Rejection reason */}
      {loan.status === "REJECTED" && loan.rejectedReason && (
        <div className="border-t border-[#e2e8f0] bg-[#fef2f2] px-5 py-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-[#dc2626] shrink-0 mt-0.5" />
          <p className="text-xs text-[#dc2626]">{loan.rejectedReason}</p>
        </div>
      )}

      {/* Approved — awaiting release */}
      {loan.status === "APPROVED" && (
        <div className="border-t border-[#e2e8f0] bg-[#fffbeb] px-5 py-3">
          <p className="text-xs text-[#a16207] font-medium">Approved — awaiting release by HR</p>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type TabValue = "ALL" | LoanStatus;

const TABS: { label: string; value: TabValue }[] = [
  { label: "All",      value: "ALL"      },
  { label: "Active",   value: "ACTIVE"   },
  { label: "Pending",  value: "PENDING"  },
  { label: "Settled",  value: "PAID"     },
  { label: "Rejected", value: "REJECTED" },
];

export default function LoanPage() {
  const [activeTab, setActiveTab] = useState<TabValue>("ACTIVE");
  const [showApplyModal, setShowApplyModal] = useState(false);
  const { toasts, toast, dismissToast } = useToast();

  const statusFilter = activeTab === "ALL" ? undefined : activeTab;
  const { data: loans, isLoading } = useMyLoans(statusFilter);
  // All loans — used for summary stats (cached by TanStack Query)
  const { data: allLoans = [] } = useMyLoans(undefined);

  const filteredLoans = loans ?? [];

  // Summary stats derived from all-loans
  const activeLoans = allLoans.filter((l) => l.status === "ACTIVE");
  const totalInstallmentPerPeriod = activeLoans.reduce((s, l) => s + (l.installmentAmount ?? 0), 0);
  const pendingCount = allLoans.filter((l) => l.status === "PENDING" || l.status === "APPROVED").length;

  const handleApplySuccess = () => {
    setShowApplyModal(false);
    toast.success("Loan application submitted successfully.");
  };

  // Grouped view for "All" tab
  const groupedLoans = (() => {
    if (activeTab !== "ALL") return null;
    const active   = filteredLoans.filter((l) => l.status === "ACTIVE");
    const inFlight = filteredLoans.filter((l) => l.status === "PENDING" || l.status === "APPROVED");
    const history  = filteredLoans.filter((l) => l.status === "PAID" || l.status === "REJECTED");
    return { active, inFlight, history };
  })();

  return (
    <div className="space-y-5">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Summary bar ───────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-[#c7d2fe] bg-gradient-to-br from-[#eff3ff] to-[#e0e7ff] p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2447d9]">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#0f172a]">Loan Application</h1>
              <p className="text-xs text-[#64748b]">Apply for salary loans and track repayments</p>
            </div>
          </div>
          <div className="shrink-0">
            <button
              onClick={() => setShowApplyModal(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              Apply for Loan
            </button>
            {pendingCount > 0 && (
              <p className="text-[10px] text-[#6366f1] mt-1 text-right">{pendingCount} awaiting approval</p>
            )}
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-xl bg-white/60 px-3 py-2.5">
            <div className="flex items-center gap-1 mb-1">
              <Banknote className="h-3 w-3 text-[#4f46e5]" />
              <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide">Active Loans</p>
            </div>
            <p className={`text-sm font-bold ${activeLoans.length > 0 ? "text-[#2447d9]" : "text-[#94a3b8]"}`}>
              {activeLoans.length > 0 ? `${activeLoans.length} loan${activeLoans.length !== 1 ? "s" : ""}` : "None"}
            </p>
            <p className="text-[10px] text-[#94a3b8] mt-0.5">Currently active</p>
          </div>
          <div className="rounded-xl bg-white/60 px-3 py-2.5">
            <div className="flex items-center gap-1 mb-1">
              <TrendingDown className="h-3 w-3 text-[#4f46e5]" />
              <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide">Per-Period Deduction</p>
            </div>
            <p className={`text-sm font-bold ${totalInstallmentPerPeriod > 0 ? "text-[#dc2626]" : "text-[#94a3b8]"}`}>
              {totalInstallmentPerPeriod > 0 ? formatCurrency(totalInstallmentPerPeriod) : "—"}
            </p>
            <p className="text-[10px] text-[#94a3b8] mt-0.5">From payslip each period</p>
          </div>
          <div className="col-span-2 sm:col-span-1 rounded-xl bg-white/60 px-3 py-2.5">
            <div className="flex items-center gap-1 mb-1">
              <Calendar className="h-3 w-3 text-[#4f46e5]" />
              <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide">Total Applications</p>
            </div>
            <p className="text-sm font-bold text-[#374151]">{allLoans.length}</p>
            <p className="text-[10px] text-[#94a3b8] mt-0.5">All time</p>
          </div>
        </div>
      </div>

      {/* ── Tab filter ────────────────────────────────────────────────────── */}
      <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={clsx(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
              activeTab === tab.value
                ? "bg-white text-[#0f172a] shadow-sm"
                : "text-[#64748b] hover:text-[#0f172a]",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" className="text-[#2447d9]" />
        </div>
      ) : filteredLoans.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-[#e2e8f0] bg-white py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#eff3ff]">
            <CreditCard className="h-8 w-8 text-[#2447d9]" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[#0f172a]">
              {activeTab === "ALL" ? "No loan applications yet" : `No ${STATUS_LABELS[activeTab as LoanStatus] ?? activeTab.toLowerCase()} loans`}
            </h2>
            <p className="mt-1 text-sm text-[#64748b] max-w-xs">
              {activeTab === "ALL"
                ? "Tap 'Apply for Loan' above to get started."
                : "Switch to 'All' to see your full loan history."}
            </p>
          </div>
        </div>
      ) : groupedLoans ? (
        /* All tab — grouped sections */
        <div className="space-y-5">
          {groupedLoans.active.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">Active</p>
              {groupedLoans.active.map((loan) => <LoanCard key={loan.loanId} loan={loan} />)}
            </section>
          )}
          {groupedLoans.inFlight.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">Pending / Approved</p>
              {groupedLoans.inFlight.map((loan) => <LoanCard key={loan.loanId} loan={loan} />)}
            </section>
          )}
          {groupedLoans.history.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">History</p>
              {groupedLoans.history.map((loan) => <LoanCard key={loan.loanId} loan={loan} />)}
            </section>
          )}
        </div>
      ) : (
        /* Single-status filtered view */
        <div className="space-y-3">
          {filteredLoans.map((loan) => <LoanCard key={loan.loanId} loan={loan} />)}
        </div>
      )}

      <ApplyLoanModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onSuccess={handleApplySuccess}
      />
    </div>
  );
}
